import { useEffect, useRef, useState } from "react";
import { isNative } from "../../../native/platform";
import { transcribeAudio } from "./api";
import { speechLanguage } from "./format";
import { toWav } from "./wav";

// Recordings sent for transcription stop by themselves after this long.
const MAX_RECORDING_MS = 5 * 60 * 1000;
// Errors after which live recognition will not work on this device; recording takes over.
const LIVE_BROKEN = ["not-allowed", "service-not-allowed", "network", "language-not-supported"];

const SpeechRecognition =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
// The app's web views expose the API without a working speech service behind it.
const liveSupported = Boolean(SpeechRecognition) && !isNative;
const recordingSupported =
  typeof window !== "undefined" &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  typeof window.MediaRecorder !== "undefined";

/**
 * Speech to text. Live recognition in the browser where it exists (words appear while
 * speaking); otherwise the recording is transcribed on the server (`serverAvailable`).
 * With `translate` (the user's "Translate voice input into" preference) the server always
 * transcribes, since the browser's recognition cannot translate.
 * Every finished piece of text goes to `onText`. `transcribe(wav, language)` sends recordings to
 * another module's endpoint (default: the Aufmaß one).
 */
const useDictation = ({
  language,
  onText,
  onError,
  serverAvailable,
  translate = false,
  transcribe = transcribeAudio
}) => {
  const [status, setStatus] = useState("idle"); // idle | starting | listening | recording | processing
  const [interim, setInterim] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [liveBroken, setLiveBroken] = useState(false);
  const recognition = useRef(null);
  const wanted = useRef(false);
  const recorder = useRef(null);
  const timers = useRef({});
  const callbacks = useRef({ onText, onError });
  callbacks.current = { onText, onError };

  const liveMode =
    liveSupported && !liveBroken && !(translate && recordingSupported && serverAvailable);
  const available = liveMode || (recordingSupported && serverAvailable);

  const clearTimers = () => {
    clearInterval(timers.current.tick);
    clearTimeout(timers.current.limit);
  };

  const startLive = () => {
    const rec = new SpeechRecognition();
    rec.lang = speechLanguage(language);
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = event => {
      let pending = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) callbacks.current.onText(result[0].transcript.trim());
        else pending += result[0].transcript;
      }
      setInterim(pending);
    };
    rec.onerror = event => {
      if (event.error === "no-speech" || event.error === "aborted") return;
      wanted.current = false;
      if (LIVE_BROKEN.includes(event.error)) setLiveBroken(true);
      callbacks.current.onError?.(event.error === "not-allowed" ? "microphone" : "live");
    };
    // Browsers end recognition after a pause; keep listening until the user stops.
    rec.onend = () => {
      if (wanted.current) {
        try {
          rec.start();
          return;
        } catch {
          // fall through to idle
        }
      }
      setInterim("");
      setStatus("idle");
    };
    recognition.current = rec;
    wanted.current = true;
    rec.start();
    setStatus("listening");
  };

  const finishRecording = async blob => {
    setStatus("processing");
    try {
      const wav = await toWav(blob);
      const res = await transcribe(wav, language);
      if (res.data.text) callbacks.current.onText(res.data.text);
      else callbacks.current.onError?.("empty");
    } catch (err) {
      callbacks.current.onError?.("server", err);
    } finally {
      setStatus("idle");
    }
  };

  const startRecording = async () => {
    // Asking for the microphone can take a moment (or a permission dialog); a second tap
    // meanwhile must not start a second recorder.
    setStatus("starting");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      });
    } catch {
      setStatus("idle");
      callbacks.current.onError?.("microphone");
      return;
    }
    const chunks = [];
    let rec;
    try {
      rec = new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach(track => track.stop());
      setStatus("idle");
      callbacks.current.onError?.("unsupported");
      return;
    }
    rec.ondataavailable = event => event.data.size && chunks.push(event.data);
    rec.onstop = () => {
      stream.getTracks().forEach(track => track.stop());
      clearTimers();
      if (chunks.length) finishRecording(new Blob(chunks, { type: rec.mimeType }));
      else setStatus("idle");
    };
    recorder.current = rec;
    rec.start();
    const started = Date.now();
    setElapsed(0);
    timers.current.tick = setInterval(() => setElapsed(Date.now() - started), 250);
    timers.current.limit = setTimeout(
      () => rec.state !== "inactive" && rec.stop(),
      MAX_RECORDING_MS
    );
    setStatus("recording");
  };

  const start = () => {
    if (status !== "idle") return;
    if (liveMode) {
      try {
        startLive();
        return;
      } catch {
        setLiveBroken(true);
      }
    }
    if (recordingSupported && serverAvailable) startRecording();
    else callbacks.current.onError?.("unsupported");
  };

  const stop = () => {
    wanted.current = false;
    if (status === "listening") recognition.current?.stop();
    if (status === "recording" && recorder.current?.state !== "inactive") recorder.current.stop();
  };

  useEffect(
    () => () => {
      wanted.current = false;
      recognition.current?.abort?.();
      if (recorder.current?.state === "recording") {
        recorder.current.onstop = null;
        recorder.current.stop();
        recorder.current.stream?.getTracks().forEach(track => track.stop());
      }
      clearTimers();
    },
    []
  );

  return {
    status,
    interim,
    elapsed,
    available,
    mode: liveMode ? "live" : "recording",
    start,
    stop,
    maxMs: MAX_RECORDING_MS
  };
};

export default useDictation;
